import Avatar from 'components/common/Avatar';
import Flex from 'components/common/Flex';
import PropTypes from 'prop-types';
import { useMemo, useState, useRef, useEffect } from 'react';
import { Card, Form, Button, InputGroup } from 'react-bootstrap';
import { useDonors } from 'hooks/useDonors';
import { useFormContext, Controller } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

/* -------------------------------------------------------------------------- */

const ADSelectDonor = ({
  title = 'Select Donor',
  editable = true
}) => {
  const { donors, isLoading: donorsLoading } = useDonors();
  const { control } = useFormContext();
  const [searchText, setSearchText] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const donorsOptions = useMemo(() => {
    if (!donors?.length) {
      return [];
    }

    return donors.map(donor => {
      const name = donor.first_name + ' ' + donor.last_name;
      const id = donor.id;
      const profileImage = donor.image;
      const score = donor.score ?? 0;

      return {
        value: id,
        name,
        profileImage,
        score
      };
    });
  }, [donors]);

  const filteredOptions = useMemo(() => {
    if (!searchText) return donorsOptions;
    return donorsOptions.filter(option =>
      option.name.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [donorsOptions, searchText]);

  const getSelectedDonor = (value) => {
    return donorsOptions.find(option => option.value === value);
  };

  return (
    <Card className="mb-3">
      <Card.Header className="pb-0">
        <Card.Title className="fw-medium">{title}</Card.Title>
      </Card.Header>
      <Card.Body>
        {editable && (
          <Controller
            name="donorId"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              const selectedDonor = getSelectedDonor(value);

              return (
                <Form.Group>
                  <Form.Label className="fs--1">
                    Donor Name <span className="text-danger">*</span>
                  </Form.Label>

                  {/* Search Input */}
                  <div ref={dropdownRef} className="position-relative">
                    <InputGroup className="mb-2">
                      <InputGroup.Text className="bg-transparent border-end-0">
                        <FontAwesomeIcon icon="search" className="text-400" />
                      </InputGroup.Text>
                      <Form.Control
                        ref={searchInputRef}
                        type="text"
                        placeholder="Search donors..."
                        value={searchText}
                        className="border-start-0 fs--1"
                        onChange={(e) => {
                          setSearchText(e.target.value);
                          setIsDropdownOpen(true);
                        }}
                        onFocus={() => setIsDropdownOpen(true)}
                      />
                    </InputGroup>

                    {/* Dropdown List */}
                    {isDropdownOpen && (
                      <div
                        className="position-absolute w-100 bg-white border rounded shadow-sm"
                        style={{
                          maxHeight: '250px',
                          overflowY: 'auto',
                          zIndex: 1000
                        }}
                      >
                        {donorsLoading ? (
                          <div className="p-3 text-center text-muted fs--1">
                            Loading donors...
                          </div>
                        ) : filteredOptions.length === 0 ? (
                          <div className="p-3 text-center text-muted fs--1">
                            No donors found
                          </div>
                        ) : (
                          filteredOptions.map(option => (
                            <div
                              key={option.value}
                              className={`p-2 cursor-pointer ${
                                value === option.value ? 'bg-primary bg-opacity-10' : 'hover-bg-light'
                              }`}
                              style={{ cursor: 'pointer' }}
                              onClick={() => {
                                onChange(option.value);
                                setSearchText('');
                                setIsDropdownOpen(false);
                              }}
                              onMouseEnter={(e) => {
                                if (value !== option.value) {
                                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (value !== option.value) {
                                  e.currentTarget.style.backgroundColor = '';
                                }
                              }}
                            >
                              <Flex alignItems="center">
                                {option.profileImage ? (
                                  <Avatar
                                    src={option.profileImage}
                                    name={option.name}
                                    size="xl"
                                    className="me-2"
                                  />
                                ) : (
                                  <Avatar size="xl" name={option.name} className="me-2" />
                                )}
                                <div className="ms-2">
                                  <span className="mb-0 fs--1 fw-semi-bold">{option.name}</span>
                                  <p className="fs--2 mb-0 text-500">score: {option.score}</p>
                                </div>
                              </Flex>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>

                  {/* Selected Donor Display */}
                  {selectedDonor && (
                    <div className="d-flex align-items-center justify-content-between p-3 border rounded bg-light">
                      <Flex alignItems="center">
                        {selectedDonor.profileImage ? (
                          <Avatar
                            src={selectedDonor.profileImage}
                            name={selectedDonor.name}
                            size="2xl"
                            className="me-2"
                          />
                        ) : (
                          <Avatar size="2xl" name={selectedDonor.name} className="me-2" />
                        )}
                        <div className="ms-2">
                          <span className="mb-0 fs-0 fw-semi-bold">{selectedDonor.name}</span>
                          <p className="fs--2 mb-0 text-500">score: {selectedDonor.score}</p>
                        </div>
                      </Flex>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="fs--1"
                        onClick={() => {
                          onChange(null);
                          setIsDropdownOpen(true);
                          setTimeout(() => searchInputRef.current?.focus(), 100);
                        }}
                      >
                        <FontAwesomeIcon icon="exchange-alt" className="me-1" />
                        Change
                      </Button>
                    </div>
                  )}

                  {error && (
                    <Form.Control.Feedback className="d-block" type="invalid">
                      {error.message}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              );
            }}
          />
        )}
      </Card.Body>
    </Card>
  );
};

ADSelectDonor.propTypes = {
  title: PropTypes.string,
  editable: PropTypes.bool,
  donorId: PropTypes.string
};

export default ADSelectDonor;
